// Anwer Fancy Lace — Pakistan cities & PostEx serviceable-area registry.
// Two structures are exported:
//   1. PAKISTAN_CITIES  — flat string list used across Checkout, Wholesale, MyAccount, Admin.
//                         Kept as-is for backward compatibility of existing <select> usages.
//   2. POSTEX_CITIES    — structured list prepared for PostEx integration. Each entry:
//                         { name, province, is_postex_serviceable, cod_supported, hub }
//                         PostEx courier docs: https://postex.pk/
//                         When the operator provides PostEx API credentials, the checkout
//                         flow can be switched to `POSTEX_CITIES.filter(c => c.is_postex_serviceable)`
//                         without changing the UI contract.
// NOTE: No PostEx API keys are hard-coded here — credentials MUST come from backend env.

export const PAKISTAN_CITIES = [
  "Lahore",
  "Karachi",
  "Islamabad",
  "Rawalpindi",
  "Faisalabad",
  "Multan",
  "Peshawar",
  "Quetta",
  "Gujranwala",
  "Sialkot",
  "Hyderabad",
  "Bahawalpur",
  "Sargodha",
  "Abbottabad",
  "Gujrat",
  "Sahiwal",
  "Sukkur",
  "Jhang",
  "Sheikhupura",
  "Rahim Yar Khan",
  "Mardan",
  "Kasur",
  "Dera Ghazi Khan",
  "Muzaffarabad",
  "Mirpur (AJK)",
  "Swat / Mingora",
  "Okara",
  "Chiniot",
  "Wah Cantt"
];

// Structured city registry — prepared for PostEx courier integration.
// `is_postex_serviceable` flags are based on PostEx's published nationwide coverage
// of major urban and secondary Pakistani cities. Final serviceability must be
// re-validated against the live PostEx `/services/api/cities` endpoint once
// credentials are configured on the backend.
export const POSTEX_CITIES = [
  { name: "Lahore",          province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "LHE" },
  { name: "Karachi",         province: "Sindh",        is_postex_serviceable: true,  cod_supported: true, hub: "KHI" },
  { name: "Islamabad",       province: "Federal",      is_postex_serviceable: true,  cod_supported: true, hub: "ISB" },
  { name: "Rawalpindi",      province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "ISB" },
  { name: "Faisalabad",      province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "FSD" },
  { name: "Multan",          province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "MUX" },
  { name: "Peshawar",        province: "KPK",          is_postex_serviceable: true,  cod_supported: true, hub: "PEW" },
  { name: "Quetta",          province: "Balochistan",  is_postex_serviceable: true,  cod_supported: true, hub: "UET" },
  { name: "Gujranwala",      province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "LHE" },
  { name: "Sialkot",         province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "LHE" },
  { name: "Hyderabad",       province: "Sindh",        is_postex_serviceable: true,  cod_supported: true, hub: "KHI" },
  { name: "Bahawalpur",      province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "MUX" },
  { name: "Sargodha",        province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "FSD" },
  { name: "Abbottabad",      province: "KPK",          is_postex_serviceable: true,  cod_supported: true, hub: "ISB" },
  { name: "Gujrat",          province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "LHE" },
  { name: "Sahiwal",         province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "MUX" },
  { name: "Sukkur",          province: "Sindh",        is_postex_serviceable: true,  cod_supported: true, hub: "KHI" },
  { name: "Jhang",           province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "FSD" },
  { name: "Sheikhupura",     province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "LHE" },
  { name: "Rahim Yar Khan",  province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "MUX" },
  { name: "Mardan",          province: "KPK",          is_postex_serviceable: true,  cod_supported: true, hub: "PEW" },
  { name: "Kasur",           province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "LHE" },
  { name: "Dera Ghazi Khan", province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "MUX" },
  { name: "Muzaffarabad",    province: "AJK",          is_postex_serviceable: false, cod_supported: false, hub: null  },
  { name: "Mirpur (AJK)",    province: "AJK",          is_postex_serviceable: true,  cod_supported: true, hub: "ISB" },
  { name: "Swat / Mingora",  province: "KPK",          is_postex_serviceable: true,  cod_supported: true, hub: "PEW" },
  { name: "Okara",           province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "LHE" },
  { name: "Chiniot",         province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "FSD" },
  { name: "Wah Cantt",       province: "Punjab",       is_postex_serviceable: true,  cod_supported: true, hub: "ISB" }
];

// Helper — used by future PostEx integration to check if a shipping city is serviceable.
export function isPostExServiceable(cityName) {
  if (!cityName) return false;
  const match = POSTEX_CITIES.find(
    (c) => c.name.toLowerCase() === String(cityName).toLowerCase()
  );
  return Boolean(match && match.is_postex_serviceable);
}

export const ORDER_STATUSES = [
  { value: "pending", label: "Pending", color: "bg-amber-100 text-amber-800" },
  { value: "confirmed", label: "Confirmed", color: "bg-blue-100 text-blue-800" },
  { value: "processing", label: "Processing / Packing", color: "bg-purple-100 text-purple-800" },
  { value: "shipped", label: "Shipped / In Transit", color: "bg-indigo-100 text-indigo-800" },
  { value: "delivered", label: "Delivered", color: "bg-emerald-100 text-emerald-800" },
  { value: "cancelled", label: "Cancelled", color: "bg-rose-100 text-rose-800" }
];
